/**
 * WebGL2 renderer: one full-screen triangle and the folding fragment shader.
 */
import { packPerm } from '../geometry/colouring';
import { FRAGMENT_SHADER, VERTEX_SHADER } from './shader';
import {
  type FrameParams,
  type SceneSettings,
  averageColour,
  modelIndex,
  packedPairs,
  triangleSize,
} from './scene';

export class WebGLUnavailableError extends Error {}

type Uniforms = Record<string, WebGLUniformLocation | null>;

const UNIFORMS = [
  'u_full',
  'u_offset',
  'u_camCenter',
  'u_camScale',
  'u_model',
  'u_morph',
  'u_view',
  'u_n1',
  'u_circle',
  'u_rotation',
  'u_scheme',
  'u_k',
  'u_pair',
  'u_baseInv',
  'u_motif',
  'u_texOrigin',
  'u_texScale',
  'u_texSize',
  'u_ground',
  'u_pal',
  'u_classGround',
  'u_classInk',
  'u_avg',
  'u_triSize',
  'u_grid',
  'u_gold',
  'u_line',
  'u_tileOutline',
  'u_domainOutline',
  'u_focus',
  'u_dpr',
  'u_bg',
];

function compile(gl: WebGL2RenderingContext, type: number, src: string): WebGLShader {
  const sh = gl.createShader(type);
  if (!sh) throw new WebGLUnavailableError('Could not create a shader');
  gl.shaderSource(sh, src);
  gl.compileShader(sh);
  if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS) && !gl.isContextLost()) {
    const log = gl.getShaderInfoLog(sh);
    gl.deleteShader(sh);
    throw new WebGLUnavailableError(`Shader failed to compile: ${log}`);
  }
  return sh;
}

export class GLRenderer {
  readonly canvas: HTMLCanvasElement;
  readonly gl: WebGL2RenderingContext;
  private program: WebGLProgram;
  private u: Uniforms = {};
  private texture: WebGLTexture;
  private texSize: [number, number] = [1, 1];
  private settings: SceneSettings | null = null;
  readonly maxTextureSize: number;

  constructor(canvas: HTMLCanvasElement) {
    this.canvas = canvas;
    const gl = canvas.getContext('webgl2', {
      alpha: true,
      antialias: false,
      depth: false,
      stencil: false,
      premultipliedAlpha: true,
      preserveDrawingBuffer: false,
      powerPreference: 'high-performance',
    });
    if (!gl) throw new WebGLUnavailableError('WebGL2 is not available');
    this.gl = gl;
    this.maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE) as number;
    const vs = compile(gl, gl.VERTEX_SHADER, VERTEX_SHADER);
    const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT_SHADER);
    const prog = gl.createProgram();
    if (!prog) throw new WebGLUnavailableError('Could not create a program');
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.bindAttribLocation(prog, 0, 'a_pos');
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS) && !gl.isContextLost()) {
      throw new WebGLUnavailableError(`Program failed to link: ${gl.getProgramInfoLog(prog)}`);
    }
    this.program = prog;
    gl.useProgram(prog);
    for (const name of UNIFORMS) this.u[name] = gl.getUniformLocation(prog, name);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
    gl.enableVertexAttribArray(0);
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);

    const tex = gl.createTexture();
    if (!tex) throw new WebGLUnavailableError('Could not create a texture');
    this.texture = tex;
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tex);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR_MIPMAP_LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, 1, 1, 0, gl.RGBA, gl.UNSIGNED_BYTE, new Uint8Array(4));
    gl.uniform1i(this.u.u_motif, 0);
  }

  /** Uploads the paint layer (premultiplied, no colour conversion) and rebuilds its mipmaps. */
  uploadMotif(source: TexImageSource & { width: number; height: number }): void {
    const gl = this.gl;
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_COLORSPACE_CONVERSION_WEBGL, gl.NONE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.generateMipmap(gl.TEXTURE_2D);
    this.texSize = [source.width, source.height];
  }

  setScene(s: SceneSettings): void {
    this.settings = s;
    const gl = this.gl;
    const u = this.u;
    gl.useProgram(this.program);
    const { tg, palette } = s;
    gl.uniform2f(u.u_n1, tg.n1.re, tg.n1.im);
    gl.uniform3f(u.u_circle, tg.center.re, tg.center.im, tg.radius);
    gl.uniform1i(u.u_rotation, s.rotation ? 1 : 0);
    const scheme = s.scheme === 'parity' ? 1 : s.scheme === 'classes' && s.colouring ? 2 : 0;
    gl.uniform1i(u.u_scheme, scheme);
    gl.uniform1i(u.u_k, s.colouring ? s.colouring.k : 1);
    gl.uniform1uiv(u.u_pair, packedPairs(s.colouring));
    gl.uniform2f(u.u_texOrigin, s.box.x0, s.box.y0);
    gl.uniform2f(u.u_texScale, 1 / (s.box.x1 - s.box.x0), 1 / (s.box.y1 - s.box.y0));
    gl.uniform3fv(u.u_ground, palette.ground);
    gl.uniform3fv(u.u_pal, palette.paint.flat());
    gl.uniform3fv(u.u_classGround, palette.classGround.flat());
    gl.uniform3fv(u.u_classInk, palette.classInk.flat());
    gl.uniform3fv(u.u_avg, averageColour(s));
    gl.uniform1f(u.u_triSize, triangleSize(tg));
    gl.uniform1i(u.u_grid, s.grid === 'tiles' ? 1 : s.grid === 'mirrors' ? 2 : 0);
    gl.uniform3fv(u.u_gold, palette.gold);
    gl.uniform3fv(u.u_line, palette.line);
  }

  private applyFrame(f: FrameParams, fullW: number, fullH: number, offX: number, offY: number) {
    const gl = this.gl;
    const u = this.u;
    gl.useProgram(this.program);
    gl.uniform2f(u.u_full, fullW, fullH);
    gl.uniform2f(u.u_offset, offX, offY);
    gl.uniform2f(u.u_camCenter, f.camera.cx, f.camera.cy);
    gl.uniform1f(u.u_camScale, f.camera.scale / f.pixelRatio);
    gl.uniform1i(u.u_model, modelIndex(f.model));
    gl.uniform1f(u.u_morph, f.morph);
    const v = f.viewInverse;
    gl.uniform4f(u.u_view, v.a.re, v.a.im, v.b.re, v.b.im);
    gl.uniform1ui(u.u_baseInv, packPerm(f.baseInverse.length ? f.baseInverse : [0]));
    gl.uniform2f(u.u_texSize, this.texSize[0], this.texSize[1]);
    gl.uniform1f(u.u_tileOutline, f.tileOutline);
    gl.uniform1f(u.u_domainOutline, f.domainOutline);
    gl.uniform1f(u.u_focus, f.focus);
    gl.uniform1f(u.u_dpr, f.pixelRatio);
    gl.uniform4fv(u.u_bg, f.background);
  }

  /** Draws into the canvas, resizing its backing store to camera size × pixelRatio. */
  render(f: FrameParams): void {
    if (!this.settings) return;
    const gl = this.gl;
    const w = Math.max(1, Math.round(f.camera.width * f.pixelRatio));
    const h = Math.max(1, Math.round(f.camera.height * f.pixelRatio));
    if (this.canvas.width !== w || this.canvas.height !== h) {
      this.canvas.width = w;
      this.canvas.height = h;
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, w, h);
    this.applyFrame(f, w, h, 0, 0);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
  }

  /** Reads RGBA pixels (device px, y down) from the frame just rendered. */
  readPixel(x: number, y: number): [number, number, number, number] {
    const gl = this.gl;
    const out = new Uint8Array(4);
    gl.readPixels(
      Math.round(x),
      this.canvas.height - 1 - Math.round(y),
      1,
      1,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      out,
    );
    return [out[0], out[1], out[2], out[3]];
  }

  /**
   * Renders an image of `size` × `size`·aspect device pixels in tiles through a framebuffer and
   * returns straight-alpha RGBA rows (top row first), ready for ImageData.
   */
  async renderImage(
    f: FrameParams,
    width: number,
    height: number,
    onProgress?: (done: number) => void,
  ): Promise<Uint8ClampedArray> {
    const gl = this.gl;
    const tile = Math.min(1024, this.maxTextureSize);
    const fbTex = gl.createTexture();
    const fb = gl.createFramebuffer();
    if (!fbTex || !fb) throw new Error('Could not allocate an export buffer');
    gl.bindTexture(gl.TEXTURE_2D, fbTex);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA8, tile, tile, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, fbTex, 0);
    gl.bindTexture(gl.TEXTURE_2D, this.texture);
    const out = new Uint8ClampedArray(width * height * 4);
    const buf = new Uint8Array(tile * tile * 4);
    const tilesX = Math.ceil(width / tile);
    const tilesY = Math.ceil(height / tile);
    let done = 0;
    try {
      for (let ty = 0; ty < tilesY; ty++) {
        for (let tx = 0; tx < tilesX; tx++) {
          const ox = tx * tile;
          const oy = ty * tile; // from the bottom
          const tw = Math.min(tile, width - ox);
          const th = Math.min(tile, height - oy);
          gl.bindFramebuffer(gl.FRAMEBUFFER, fb);
          gl.viewport(0, 0, tw, th);
          this.applyFrame(f, width, height, ox, oy);
          gl.drawArrays(gl.TRIANGLES, 0, 3);
          gl.readPixels(0, 0, tw, th, gl.RGBA, gl.UNSIGNED_BYTE, buf);
          for (let row = 0; row < th; row++) {
            const destRow = height - 1 - (oy + row);
            for (let col = 0; col < tw; col++) {
              const s = (row * tw + col) * 4;
              const d = (destRow * width + ox + col) * 4;
              const a = buf[s + 3];
              if (a === 0) {
                out[d + 3] = 0;
              } else {
                const k = 255 / a; // un-premultiply
                out[d] = buf[s] * k;
                out[d + 1] = buf[s + 1] * k;
                out[d + 2] = buf[s + 2] * k;
                out[d + 3] = a;
              }
            }
          }
          done++;
          onProgress?.(done / (tilesX * tilesY));
          await new Promise((r) => setTimeout(r, 0));
        }
      }
    } finally {
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.deleteFramebuffer(fb);
      gl.deleteTexture(fbTex);
      gl.bindTexture(gl.TEXTURE_2D, this.texture);
    }
    return out;
  }

  dispose(): void {
    const ext = this.gl.getExtension('WEBGL_lose_context');
    ext?.loseContext();
  }
}
